import { showEvents } from "@/Configs/config";
import { useModal } from "@/Contexts";
import { useGlobalAlert } from "@/States/globalAlert";
import { getInfrastructureUrl } from "@/Utils/getInfrastructureUrl";
import { trpc } from "@/Utils/trpc";
import { useNavigate } from "@/Utils/useNavigate";
import { Button } from "@/components/button";
import { Card, CardComponentVariant } from "@/components/pageCards/card";
import { CardInfo } from "@/components/pageCards/cardInfo";
import { useAccessPoints } from "@/pages/infrastructure/manage/useAccessPoints";
import { DeletePointModal } from "@/pages/infrastructure/modals/deletePointModal";
import { EditDepotModal } from "@/pages/infrastructure/modals/editDepotModal";
import { InvalidDeletePointModal } from "@/pages/infrastructure/modals/invalidDeletePointModal";
import { useTrans } from "@/translations";

import { FC } from "react";
import { useParams } from "react-router-dom";

import { MultiListDrawer } from "../../multiListDrawer";
import { PreFilteringCard } from "./drawerCards/PreFilteringCard";

export const DepotDrawer: FC<{
  accessParentId: string;
  depotId: string;
}> = ({ accessParentId, depotId }) => {
  const { t } = useTrans();
  const { showModal } = useModal();

  const { terminalId: urlTerminalId } = useParams();

  const { isLoading, isError, getDepot, refetch } = useAccessPoints({
    accessParentId,
  });
  const depot = getDepot(depotId);

  const { data: customers } = trpc.customers.getAll.useQuery({
    pointIds: [depotId],
    limit: 1,
  });

  const { mutateAsync: deleteDepot } = trpc.depots.delete.useMutation();

  const navigate = useNavigate();
  const { setGlobalAlert } = useGlobalAlert();

  const openEditModal = () => {
    if (!depot) return;
    showModal(
      <EditDepotModal
        depot={depot}
        onSuccess={() => {
          refetch();
        }}
      />,
    );
  };

  const openDeletionModal = () => {
    if (customers?.items.length || depot?.containers?.length) {
      showModal(
        <InvalidDeletePointModal
          {...{
            modalTitle: "actions:deleteGroup",
            invalidDeleteHint: "hints:deleteGroupWarning",
          }}
        />,
      );
    } else {
      showModal(
        <DeletePointModal
          {...{
            accessParentId,
            pointId: depot?.id || "",
            pointName: depot?.name,
            modalTitle: "actions:deleteGroup",
            deleteFunction: deleteDepot,
            onSuccess: () => {
              setGlobalAlert({
                type: "success",
                message: "systemMessages:groupDeleted",
              });

              const url = getInfrastructureUrl({
                terminalId: urlTerminalId,
                accessParentId,
              });
              navigate(url);
            },
          }}
        />,
      );
    }
  };

  return (
    <MultiListDrawer {...{ isLoading, isError }}>
      {depot?.name && (
        <h4 className="font-signifier text-3xl font-light text-black">
          {depot?.name}
        </h4>
      )}
      <Card
        variant={CardComponentVariant.DRAWER}
        title="drawerLabels:groupDetails"
      >
        <div className="flex flex-col gap-4">
          <CardInfo heading={t("importLabels:depotName")} value={depot?.name} />
          <CardInfo
            heading={t("formLabels:status")}
            value={t(`status:${depot?.status}`)}
          />
          <CardInfo
            heading={t("drawerLabels:carrotAccessParentID")}
            value={depot?.id}
          />
        </div>
        <Button
          label={"actions:edit"}
          onClick={openEditModal}
          color="secondary"
          className="float-right mt-10"
        />
      </Card>
      {showEvents && (
        <PreFilteringCard
          entityKey="depot"
          eventFilter={encodeURIComponent(JSON.stringify({ depotId }))}
        />
      )}
      <Card title={"drawerLabels:admin"} variant={CardComponentVariant.DRAWER}>
        <div className="mb-6 text-sm">{t("hints:deleteGroupWarning")}</div>
        <Button
          label={"actions:deleteGroup"}
          onClick={openDeletionModal}
          color="black"
          className="float-right"
        />
      </Card>
    </MultiListDrawer>
  );
};
